import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getPlayerCareer } from "../../../../api/league";
import {
  formatPlayerRating,
  formatSeasonStat,
  asStat,
  clubStayFromCareer,
  seasonClubName,
} from "../../../../service/playerStats";
import { rangeColor } from "../../../../service/data";
import {
  StyledPage,
  StyledBack,
  StyledHeader,
  StyledEyebrow,
  StyledTitle,
  StyledClub,
  StyledTotals,
  StyledHistory,
  StyledHistoryHeader,
  StyledTableWrap,
  StyledTable,
  StyledRatingBadge,
  StyledEmpty,
} from "./styled";

export default function PlayerCareer() {
  const { playerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getPlayerCareer(playerId);
        if (response.status === 200) {
          const json = await response.json();
          if (!cancelled) setData(json);
        } else if (!cancelled) {
          setError(
            response.status === 404
              ? "Player not found."
              : "Could not load career."
          );
        }
      } catch {
        if (!cancelled) setError("Could not load career.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  if (loading)
    return (
      <StyledPage>
        <StyledEmpty>Loading career…</StyledEmpty>
      </StyledPage>
    );
  if (error)
    return (
      <StyledPage>
        <StyledEmpty>{error}</StyledEmpty>
      </StyledPage>
    );
  if (!data) return null;

  const name = [data.player?.first_name, data.player?.last_name]
    .filter(Boolean)
    .join(" ");
  const totals = data.totals || {};
  const seasons = Array.isArray(data.seasons) ? data.seasons : [];
  const hasHistoricClubs = seasons.some((row) => seasonClubName(row));
  const stay = clubStayFromCareer(data);
  const stayClubName =
    stay?.clubName || data.current_club?.team?.name || null;
  return (
    <StyledPage>
      <StyledBack as={Link} to="/league">
        ← League
      </StyledBack>
      <StyledHeader>
        <StyledEyebrow>Career History</StyledEyebrow>
        <StyledTitle>{name || "Career History"}</StyledTitle>
        {stayClubName ? (
          <StyledClub>
            <span>Current club</span>
            <strong>{stayClubName}</strong>
            {stay?.appearances != null ? (
              <small>{stay.appearances} apps this stay</small>
            ) : null}
            {stay?.goals != null ? (
              <small>{stay.goals} goals this stay</small>
            ) : null}
            {stay?.assists != null ? (
              <small>{stay.assists} assists this stay</small>
            ) : null}
            {stay?.playtimeThisSeason != null ? (
              <small>
                {Number(stay.playtimeThisSeason).toFixed(2)} playtime
              </small>
            ) : null}
          </StyledClub>
        ) : null}
      </StyledHeader>
      <StyledTotals>
        <div>
          <strong>{asStat(totals.appearances)}</strong>
          <span>Appearances</span>
        </div>
        <div>
          <strong>
            {asStat(totals.goals)}
            {totals.goals_cup != null
              ? ` + ${asStat(totals.goals_cup)} cup`
              : ""}
          </strong>
          <span>Goals (L + cup)</span>
        </div>
        <div>
          <strong>
            {asStat(totals.assists)}
            {totals.assists_cup != null
              ? ` + ${asStat(totals.assists_cup)} cup`
              : ""}
          </strong>
          <span>Assists (L + cup)</span>
        </div>
        <div>
          <strong>
            {asStat(totals.clean_sheets)}
            {totals.clean_sheets_cup != null
              ? ` + ${asStat(totals.clean_sheets_cup)} cup`
              : ""}
          </strong>
          <span>Clean sheets</span>
        </div>
      </StyledTotals>
      <StyledHistory>
        <StyledHistoryHeader>
          <div>
            <span>Career timeline</span>
            <h3>Season-by-season</h3>
          </div>
          <small>{seasons.length} seasons</small>
        </StyledHistoryHeader>
        <StyledTableWrap>
          <StyledTable>
            <thead>
              <tr>
                <th>Season</th>
                {hasHistoricClubs ? <th>Club</th> : null}
                <th>Rating</th>
                <th>Appearances</th>
                <th>League goals</th>
                <th>Cup goals</th>
                <th>League assists</th>
                <th>Cup assists</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map((row, index) => {
                const rating = formatPlayerRating(row.rating, row.rating_range);
                return (
                  <tr key={`${row.season_number}-${index}`}>
                    <td>
                      {row.season_number == null
                        ? "—"
                        : `S${row.season_number}`}
                      {row.in_progress ? <em>Now</em> : null}
                    </td>
                    {hasHistoricClubs ? <td>{seasonClubName(row) || "—"}</td> : null}
                    <td>
                      <StyledRatingBadge $color={rangeColor[rating]}>
                        {rating}
                      </StyledRatingBadge>
                    </td>
                    <td>{formatSeasonStat(row.played_matches)}</td>
                    <td>{formatSeasonStat(row.goals_scored)}</td>
                    <td>{formatSeasonStat(row.goals_scored_cup)}</td>
                    <td>{formatSeasonStat(row.assists)}</td>
                    <td>{formatSeasonStat(row.assists_cup)}</td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </StyledTableWrap>
      </StyledHistory>
    </StyledPage>
  );
}
